import { useEffect, useRef, useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Field, InfoHint, NativeSelect, Textarea, keepEdited } from "@ncp/ui";

export type ReplyAnswer = "Accepted" | "Discounted" | "EvidenceRequested";
export const REPLY_ANSWERS: ReplyAnswer[] = ["Accepted", "Discounted", "EvidenceRequested"];

/** One point of a party's reply and the Department's answer to it, as the API returns it. */
export interface ReplyPoint {
  id: string;
  point: string;
  pointAr?: string | null;
  answer?: ReplyAnswer | null;
  reasons?: string | null;
  reasonsAr?: string | null;
  answeredAt?: string | null;
  answeredByName?: string | null;
}

/** A point as the officer enters it; an existing point keeps its id. */
export interface ReplyPointInput { id?: string | null; point: string; pointAr?: string | null; answer?: ReplyAnswer | null; reasons?: string | null; reasonsAr?: string | null }

interface Row { id?: string; point: string; pointAr: string; answer: ReplyAnswer | ""; reasons: string; reasonsAr: string; answeredAt?: string | null; answeredByName?: string | null }

const toRow = (p: ReplyPoint): Row => ({ id: p.id, point: p.point, pointAr: p.pointAr ?? "", answer: p.answer ?? "", reasons: p.reasons ?? "", reasonsAr: p.reasonsAr ?? "", answeredAt: p.answeredAt, answeredByName: p.answeredByName });
const blank = (): Row => ({ point: "", pointAr: "", answer: "", reasons: "", reasonsAr: "" });
const text = (s: string) => (s.trim() ? s.trim() : null);
const written = (r: Row) => !!(r.point.trim() || r.pointAr.trim() || r.answer || r.reasons.trim() || r.reasonsAr.trim());

/**
 * The points as refetched, with what the officer wrote kept (S 2.7): a recorded point they changed since it last came keeps their text, and a
 * point they added and have not saved stays until the server returns it saved.
 */
function mergeRows(current: Row[], last: Row[], next: Row[]): Row[] {
  const recorded = next.filter((n) => n.id).map((n) => {
    const mine = current.find((x) => x.id === n.id);
    const was = last.find((x) => x.id === n.id);
    return mine && was ? keepEdited(mine, was, n) : n;
  });
  const unsaved = current.filter((c) => !c.id && written(c) && !recorded.some((n) => n.point.trim() === c.point.trim() && n.pointAr.trim() === c.pointAr.trim()));
  const rows = [...recorded, ...unsaved];
  return rows.length > 0 ? rows : [blank()];
}

/**
 * A party's reply taken point by point: what each point says and how the Department meets it – accepted, discounted with reasons, or met with a
 * request for evidence. Used for the reply to a statement of preliminary findings and for replies to a preliminary investigation report; the
 * stage review reads that every point is answered. With `draftScope` (e.g. `case:${caseId}:reply:${replyId}`), what the officer writes on a recorded
 * point is kept on the device until it is saved (S 2.7).
 */
export function ReplyPointsEditor({ points, readOnly, saving, onSave, draftScope }: { points: ReplyPoint[]; readOnly?: boolean; saving?: boolean; onSave: (points: ReplyPointInput[]) => void; draftScope?: string | null }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [rows, setRows] = useState<Row[]>(() => (points.length > 0 ? points.map(toRow) : [blank()]));
  const loaded = useRef(rows);
  useEffect(() => {
    const next = points.length > 0 ? points.map(toRow) : [blank()];
    setRows((current) => { const merged = mergeRows(current, loaded.current, next); return JSON.stringify(merged) === JSON.stringify(current) ? current : merged; });
    loaded.current = next;
  }, [points]);
  // A recorded point is named by its id; a point not yet saved has no stable name (its place shifts as rows are removed), so it keeps none.
  const kept = (r: Row, field: string) => (draftScope && r.id ? `${draftScope}:point:${r.id}:${field}` : null);
  const set = (i: number, patch: Partial<Row>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const filled = rows.filter((r) => r.point.trim() || r.pointAr.trim());
  const answered = filled.filter((r) => r.answer && (r.reasons.trim() || r.reasonsAr.trim())).length;
  // An answer is saved only with its reasons; a point may be saved unanswered while the reply is still being read.
  const invalid = filled.some((r) => r.answer && !r.reasons.trim() && !r.reasonsAr.trim());

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:reviewChecks.replyPoints.title")}</p>
        <InfoHint>{t("backoffice:reviewChecks.replyPoints.hint")}</InfoHint>
        <Badge size="sm" variant={filled.length > 0 && answered === filled.length ? "success" : "warning"} className="ms-auto">{t("backoffice:reviewChecks.replyPoints.progress", { answered, total: filled.length })}</Badge>
      </div>
      {rows.map((r, i) => (
        <div key={r.id ?? `new-${i}`} className="rounded-lg border border-sand-200 p-3">
          <div className="grid gap-2 md:grid-cols-2">
            <Field label={`${i + 1}. ${t("backoffice:reviewChecks.replyPoints.point")}`}>{(id) => <Textarea id={id} rows={2} value={r.point} disabled={readOnly} onChange={(e) => set(i, { point: e.target.value })} dir="auto" draft={kept(r, "point")} />}</Field>
            <Field label={t("backoffice:reviewChecks.replyPoints.pointAr")}>{(id) => <Textarea id={id} rows={2} dir="rtl" value={r.pointAr} disabled={readOnly} onChange={(e) => set(i, { pointAr: e.target.value })} draft={kept(r, "pointAr")} />}</Field>
          </div>
          <div className="mt-2 grid gap-2 md:grid-cols-[14rem_1fr_1fr]">
            <Field label={t("backoffice:reviewChecks.replyPoints.answer")}>{(id) => (
              <NativeSelect id={id} value={r.answer} disabled={readOnly} onChange={(e) => set(i, { answer: e.target.value as ReplyAnswer | "" })}>
                <option value="">{t("backoffice:reviewChecks.replyPoints.unanswered")}</option>
                {REPLY_ANSWERS.map((a) => <option key={a} value={a}>{t(`backoffice:reviewChecks.replyPoints.answers.${a}`)}</option>)}
              </NativeSelect>
            )}</Field>
            <Field label={t("backoffice:reviewChecks.replyPoints.reasons")}>{(id) => <Textarea id={id} rows={2} value={r.reasons} disabled={readOnly} onChange={(e) => set(i, { reasons: e.target.value })} dir="auto" draft={kept(r, "reasons")} />}</Field>
            <Field label={t("backoffice:reviewChecks.replyPoints.reasonsAr")}>{(id) => <Textarea id={id} rows={2} dir="rtl" value={r.reasonsAr} disabled={readOnly} onChange={(e) => set(i, { reasonsAr: e.target.value })} draft={kept(r, "reasonsAr")} />}</Field>
          </div>
          <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
            <span>{r.answeredAt ? t("backoffice:reviewChecks.replyPoints.answeredBy", { date: formatDateTime(r.answeredAt, locale), by: r.answeredByName ?? "" }) : null}</span>
            {!readOnly && !r.id ? <Button size="sm" variant="ghost" onClick={() => setRows((x) => x.filter((_, j) => j !== i))}><Trash2 /> {t("backoffice:reviewChecks.replyPoints.remove")}</Button> : null}
          </div>
        </div>
      ))}
      {readOnly ? null : (
        <div className="flex flex-wrap justify-between gap-2">
          <Button size="sm" variant="outline" onClick={() => setRows((r) => [...r, blank()])}><Plus /> {t("backoffice:reviewChecks.replyPoints.add")}</Button>
          <Button size="sm" variant="gold" disabled={filled.length === 0 || invalid} loading={saving}
            onClick={() => onSave(filled.map((r) => ({ id: r.id ?? null, point: r.point.trim() || r.pointAr.trim(), pointAr: text(r.pointAr), answer: r.answer || null, reasons: text(r.reasons), reasonsAr: text(r.reasonsAr) })))}>
            <Save /> {t("backoffice:reviewChecks.replyPoints.save")}
          </Button>
        </div>
      )}
    </div>
  );
}
